import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUp, Plus } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { ConversationTurn } from "@/components/conversation-turn";
import { Spinner } from "@/components/spinner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { tools } from "@/lib/tools";

export const Route = createFileRoute("/_authed/conversations/$conversationId")({
  component: Conversation,
});
function Conversation() {
  const { conversationId } = Route.useParams();
  const client = useQueryClient();
  const [draft, setDraft] = useState("");
  const request = useRef({ prompt: "", id: crypto.randomUUID() });
  const query = useQuery({
    ...tools.queryOptions("GET /api/conversations/{id}", { id: conversationId }),
    refetchInterval: (query) =>
      query.state.data?.runs.some((run) => run.status === "queued" || run.status === "running")
        ? 2000
        : false,
  });
  const active = query.data?.runs.find(
    (run) => run.status === "queued" || run.status === "running",
  );
  const send = useMutation(
    tools.mutationOptions("POST /api/conversations/{id}/messages", {
      onSuccess: async () => {
        setDraft("");
        request.current = { prompt: "", id: crypto.randomUUID() };
        await query.refetch();
        await client.invalidateQueries({ queryKey: tools.queryKey("GET /api/conversations") });
      },
    }),
  );
  // Land on the latest turn when the conversation opens, and follow each new
  // turn after sending. Keyed on the turn count, not on streamed text, so
  // reading an earlier answer while one streams is not yanked away.
  const turnCount = query.data?.runs.length ?? 0;
  const endRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (turnCount > 0) endRef.current?.scrollIntoView({ block: "end" });
  }, [turnCount]);

  if (query.error) return <p role="alert">{query.error.message}</p>;
  if (!query.data) return <Spinner />;
  const data = query.data;
  const locked = Boolean(active) || send.isPending || !data.canContinue;
  return (
    <div className="mx-auto flex w-full max-w-3xl flex-1 flex-col px-4 md:px-8">
      <header className="space-y-1 pt-4 md:pt-8">
        <Link
          className="inline-flex items-center gap-1 text-muted-foreground text-sm hover:text-foreground"
          to="/agents"
        >
          <Plus aria-hidden className="size-3.5" />
          Neue Unterhaltung
        </Link>
        <h1 className="break-words font-serif text-2xl tracking-tight">{data.title}</h1>
      </header>
      <div className="flex-1 space-y-10 py-8">
        {data.runs.map((run) => (
          <ConversationTurn
            key={run.id}
            run={run}
            messages={data.messages
              .filter((message) => message.runId === run.id)
              .map((message) => ({ ...message.payload, messageIndex: message.messageIndex }))}
            submittedPlans={(data.submittedPlans ?? []).filter(
              (submission) => submission.runId === run.id,
            )}
            onComplete={async () => {
              await query.refetch();
              await client.invalidateQueries({
                queryKey: tools.queryKey("GET /api/conversations"),
              });
            }}
          />
        ))}
        {!data.canContinue && (
          <p className="rounded-lg bg-muted p-4 text-sm">
            Diese Unterhaltung wurde mit einer anderen Modellkonfiguration begonnen und kann nicht
            fortgesetzt werden.{" "}
            <Link className="underline decoration-accent underline-offset-4" to="/agents">
              Neue Unterhaltung starten
            </Link>
          </p>
        )}
        <div ref={endRef} />
      </div>
      <form
        className="sticky bottom-0 bg-background pt-2 pb-4 md:pb-6"
        onSubmit={(event) => {
          event.preventDefault();
          // Enter submits via requestSubmit, which ignores the disabled
          // button, so the guard has to live here rather than on the button.
          if (!draft.trim() || locked) return;
          if (request.current.prompt !== draft)
            request.current = { prompt: draft, id: crypto.randomUUID() };
          send.mutate({ id: conversationId, prompt: draft, idempotencyKey: request.current.id });
        }}
      >
        {send.error && (
          <p role="alert" className="mb-2 text-destructive text-sm">
            {send.error.message}
          </p>
        )}
        <div className="flex items-end gap-2 rounded-xl border bg-background p-2 transition-colors focus-within:border-accent focus-within:ring-2 focus-within:ring-accent/25">
          <label htmlFor="follow-up" className="sr-only">
            Folgefrage
          </label>
          <Textarea
            id="follow-up"
            submitOnEnter
            rows={1}
            className="min-h-10 border-0 px-2 py-2 focus-visible:ring-0"
            placeholder={active ? "Tycho antwortet…" : "Folgefrage stellen…"}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            disabled={locked}
          />
          <Button
            size="icon"
            aria-label="Senden"
            className="size-10 shrink-0 rounded-lg"
            disabled={!draft.trim() || locked}
          >
            <ArrowUp className="size-4" />
          </Button>
        </div>
        <p className="mt-1.5 hidden text-center text-muted-foreground text-xs md:block">
          Enter zum Senden · Umschalt+Enter für eine neue Zeile
        </p>
      </form>
    </div>
  );
}
